import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { uploadAPI, authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Upload, X, Image as ImageIcon, User as UserIcon,
  Store, Link2, Globe, ExternalLink, Eye, EyeOff, Plus, Instagram, Facebook, Youtube,
} from 'lucide-react';

const VENDOR_CATEGORIES = [
  'Art & Fine Art', 'Jewelry', 'Pottery/Ceramics', 'Woodworking',
  'Textiles/Fiber Arts', 'Candles & Soap', 'Photography', 'Leather Goods',
  'Home Decor', 'Food/Baked Goods', 'Plants/Flowers', 'Mixed Media',
  'Glass Art', 'Metalwork', 'Paper Arts', 'Apparel', 'Accessories', 'Other',
];

const MAX_GALLERY = 24;
const MAX_GALLERY_BYTES = 3 * 1024 * 1024; // 3MB
const MAX_COVER_BYTES = 5 * 1024 * 1024;   // 5MB (backend limit)

const SOCIAL_PLATFORMS = [
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourhandle', Icon: Instagram },
  { key: 'facebook',  label: 'Facebook',  placeholder: 'https://facebook.com/yourpage',   Icon: Facebook },
  { key: 'tiktok',    label: 'TikTok',    placeholder: 'https://tiktok.com/@yourhandle',   Icon: ExternalLink },
  { key: 'pinterest', label: 'Pinterest', placeholder: 'https://pinterest.com/yourboard',  Icon: ExternalLink },
  { key: 'youtube',   label: 'YouTube',   placeholder: 'https://youtube.com/@yourchannel', Icon: Youtube },
  { key: 'twitter',   label: 'X / Twitter', placeholder: 'https://x.com/yourhandle',       Icon: ExternalLink },
];

export const VendorProfileEdit = () => {
  const { user, updateSettings } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const [isPublic, setIsPublic] = useState(user?.is_public_profile ?? true);
  const [businessName, setBusinessName] = useState(user?.business_name || '');
  const [slug, setSlug] = useState(user?.slug || '');
  const [tagline, setTagline] = useState(user?.tagline || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [vendorCategory, setVendorCategory] = useState(user?.vendor_category || '');
  const [websiteUrl, setWebsiteUrl] = useState(user?.website_url || '');
  const [etsyUrl, setEtsyUrl] = useState(user?.etsy_url || '');
  const [social, setSocial] = useState(() => user?.social_links || {});
  const [coverUrl, setCoverUrl] = useState(user?.cover_photo_url || '');
  const [logoUrl, setLogoUrl] = useState(user?.logo_url || '');
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(user?.profile_photo_url || '');
  const [gallery, setGallery] = useState(user?.gallery || []);
  const [uploadingField, setUploadingField] = useState(null);

  // Refresh from /me on mount so a freshly-created slug from signup gets shown
  useEffect(() => {
    let mounted = true;
    authAPI.getMe().then((res) => {
      if (!mounted) return;
      const u = res.data;
      if (!slug && u.slug) setSlug(u.slug);
    }).catch(() => {});
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadSingle = async (file, maxBytes, fieldLabel) => {
    if (!file) return null;
    if (file.size > maxBytes) {
      toast.error(`${fieldLabel} must be under ${Math.round(maxBytes / (1024 * 1024))}MB.`);
      return null;
    }
    try {
      const res = await uploadAPI.uploadImage(file);
      return res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to upload ${fieldLabel.toLowerCase()}`);
      return null;
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingField('cover');
    const url = await uploadSingle(file, MAX_COVER_BYTES, 'Cover photo');
    if (url) setCoverUrl(url);
    setUploadingField(null);
    e.target.value = '';
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingField('logo');
    const url = await uploadSingle(file, MAX_COVER_BYTES, 'Logo');
    if (url) setLogoUrl(url);
    setUploadingField(null);
    e.target.value = '';
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingField('profile');
    const url = await uploadSingle(file, MAX_COVER_BYTES, 'Profile photo');
    if (url) setProfilePhotoUrl(url);
    setUploadingField(null);
    e.target.value = '';
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remaining = MAX_GALLERY - gallery.length;
    if (remaining <= 0) {
      toast.error(`Gallery is full — max ${MAX_GALLERY} photos.`);
      e.target.value = '';
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.info(`Only uploading the first ${remaining} (max ${MAX_GALLERY} total).`);
    }

    setUploadingField('gallery');
    const uploaded = [];
    for (const file of toUpload) {
      const url = await uploadSingle(file, MAX_GALLERY_BYTES, 'Gallery photo');
      if (url) uploaded.push(url);
    }
    if (uploaded.length > 0) {
      setGallery((g) => [...g, ...uploaded]);
      toast.success(`Added ${uploaded.length} photo${uploaded.length === 1 ? '' : 's'} to gallery.`);
    }
    setUploadingField(null);
    e.target.value = '';
  };

  const removeGalleryItem = (idx) =>
    setGallery((g) => g.filter((_, i) => i !== idx));

  const handleSocialChange = (key, value) =>
    setSocial((s) => ({ ...s, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Filter empty social URLs out of the payload
      const cleanedSocial = Object.fromEntries(
        Object.entries(social).filter(([, v]) => v && v.trim())
      );
      await updateSettings({
        business_name: businessName || null,
        slug: slug ? slug.trim().toLowerCase() : null,
        tagline: tagline || null,
        bio: bio || null,
        vendor_category: vendorCategory || null,
        website_url: websiteUrl || null,
        etsy_url: etsyUrl || null,
        social_links: Object.keys(cleanedSocial).length ? cleanedSocial : null,
        cover_photo_url: coverUrl || null,
        logo_url: logoUrl || null,
        profile_photo_url: profilePhotoUrl || null,
        gallery,
        is_public_profile: isPublic,
      });
      toast.success('Profile saved!');
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(msg || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen py-8 px-6 md:px-12" data-testid="vendor-profile-edit-page">
      <div className="max-w-4xl mx-auto">
        <Link
          to="/settings"
          className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1E3A5F] mb-6 font-medium"
          data-testid="back-to-settings"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to settings
        </Link>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Store className="w-8 h-8 text-[#1E3A5F]" />
            <div>
              <h1 className="text-3xl font-bold font-['Outfit'] text-[#1E3A5F]">Edit Vendor Profile</h1>
              <p className="text-[#4A4A4A] text-sm">
                Your public landing page at{' '}
                <span className="font-semibold text-[#1E3A5F]">
                  /v/{slug || '…'}
                </span>
              </p>
            </div>
          </div>
          {slug && (
            <Link to={`/v/${slug}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F]" data-testid="view-public-profile-btn">
                <ExternalLink className="w-4 h-4 mr-2" />
                View Public Profile
              </Button>
            </Link>
          )}
        </div>

        <form onSubmit={handleSave} className="space-y-8">
          {/* Visibility toggle */}
          <section className="card-dashboard" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-[#1E3A5F] flex items-center gap-2">
                  {isPublic ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  Profile Visibility
                </h2>
                <p className="text-sm text-[#4A4A4A] mt-1">
                  {isPublic
                    ? 'Your profile is discoverable in the Vendor Community directory and shareable via link.'
                    : 'Your profile is hidden. Nobody can view or share it.'}
                </p>
              </div>
              <Switch
                checked={isPublic}
                onCheckedChange={setIsPublic}
                data-testid="toggle-public-profile"
              />
            </div>
          </section>

          {/* Cover photo */}
          <section className="card-dashboard" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
            <h2 className="text-lg font-bold text-[#1E3A5F] mb-2 flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Cover Photo
            </h2>
            <p className="text-sm text-[#4A4A4A] mb-4">
              Horizontal banner at the top of your profile. Recommended size <strong>1600&nbsp;&times;&nbsp;540</strong>. Max 5MB.
            </p>
            <div
              className="relative rounded-xl overflow-hidden border-2 border-dashed border-gray-300 bg-gradient-to-br from-[#1E3A5F]/10 to-[#FE5F55]/10 aspect-[1600/540] flex items-center justify-center"
              data-testid="cover-preview"
            >
              {coverUrl ? (
                <>
                  <img src={coverUrl} alt="Cover" className="absolute inset-0 w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setCoverUrl('')}
                    className="absolute top-3 right-3 bg-white/90 hover:bg-white p-1.5 rounded-full shadow"
                    data-testid="remove-cover-btn"
                  >
                    <X className="w-4 h-4 text-[#1E3A5F]" />
                  </button>
                </>
              ) : (
                <div className="text-center text-[#71717A] text-sm">
                  <ImageIcon className="w-8 h-8 mx-auto mb-1" />
                  No cover yet
                </div>
              )}
            </div>
            <div className="mt-3">
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1E3A5F] text-white text-sm font-semibold cursor-pointer hover:bg-[#2d4a6f]" data-testid="upload-cover-btn">
                {uploadingField === 'cover' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {coverUrl ? 'Replace cover' : 'Upload cover'}
                <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
              </label>
            </div>
          </section>

          {/* Logo + Profile Photo side-by-side */}
          <section className="card-dashboard grid md:grid-cols-2 gap-6" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
            <PhotoTile
              label="Logo"
              url={logoUrl}
              onUpload={handleLogoUpload}
              onRemove={() => setLogoUrl('')}
              uploading={uploadingField === 'logo'}
              helper="Square-ish logo used in cards and search results."
              testid="logo"
            />
            <PhotoTile
              label="Profile Photo"
              url={profilePhotoUrl}
              onUpload={handleProfilePhotoUpload}
              onRemove={() => setProfilePhotoUrl('')}
              uploading={uploadingField === 'profile'}
              helper="A photo of you — shown as the main avatar on your profile."
              testid="profile-photo"
            />
          </section>

          {/* Identity & Story */}
          <section className="card-dashboard space-y-4" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
            <h2 className="text-lg font-bold text-[#1E3A5F] flex items-center gap-2">
              <UserIcon className="w-5 h-5" />
              Identity & Story
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="biz" className="font-medium text-[#1E3A5F]">Business / Shop Name</Label>
                <Input
                  id="biz"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="e.g., Mended Heart Glass"
                  className="input-default"
                  data-testid="edit-business-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug" className="font-medium text-[#1E3A5F]">Public URL slug</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#71717A] whitespace-nowrap">/v/</span>
                  <Input
                    id="slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="mended-heart-glass"
                    className="input-default"
                    data-testid="edit-slug"
                  />
                </div>
                <p className="text-xs text-[#71717A]">Letters, numbers, and dashes only. Must be unique.</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tagline" className="font-medium text-[#1E3A5F]">Tagline</Label>
              <Input
                id="tagline"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="One-liner that captures your craft"
                className="input-default"
                data-testid="edit-tagline"
                maxLength={120}
              />
              <p className="text-xs text-[#71717A]">{tagline.length}/120</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio" className="font-medium text-[#1E3A5F]">Bio</Label>
              <Textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell the community about your craft, your story, your booth…"
                className="input-default min-h-[140px]"
                data-testid="edit-bio"
                maxLength={1500}
              />
              <p className="text-xs text-[#71717A]">{bio.length}/1500</p>
            </div>

            <div className="space-y-2 max-w-xs">
              <Label className="font-medium text-[#1E3A5F]">Vendor Category</Label>
              <Select value={vendorCategory} onValueChange={setVendorCategory}>
                <SelectTrigger className="input-default" data-testid="edit-vendor-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {VENDOR_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          {/* Links */}
          <section className="card-dashboard space-y-4" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
            <h2 className="text-lg font-bold text-[#1E3A5F] flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Links
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-medium text-[#1E3A5F] flex items-center gap-2">
                  <Globe className="w-4 h-4" /> Website
                </Label>
                <Input
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yourstudio.com"
                  className="input-default"
                  data-testid="edit-website-url"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-medium text-[#1E3A5F] flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" /> Etsy Shop
                </Label>
                <Input
                  value={etsyUrl}
                  onChange={(e) => setEtsyUrl(e.target.value)}
                  placeholder="https://etsy.com/shop/yourshop"
                  className="input-default"
                  data-testid="edit-etsy-url"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {SOCIAL_PLATFORMS.map(({ key, label, placeholder, Icon }) => (
                <div key={key} className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F] flex items-center gap-2">
                    <Icon className="w-4 h-4" /> {label}
                  </Label>
                  <Input
                    value={social[key] || ''}
                    onChange={(e) => handleSocialChange(key, e.target.value)}
                    placeholder={placeholder}
                    className="input-default"
                    data-testid={`edit-social-${key}`}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Gallery */}
          <section className="card-dashboard" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
              <h2 className="text-lg font-bold text-[#1E3A5F] flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Gallery
                <span className="text-sm font-normal text-[#71717A]">({gallery.length}/{MAX_GALLERY})</span>
              </h2>
              <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer ${
                gallery.length >= MAX_GALLERY
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-[#FE5F55] text-white hover:bg-[#e6544a]'
              }`} data-testid="upload-gallery-btn">
                {uploadingField === 'gallery' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add photos
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleGalleryUpload}
                  disabled={gallery.length >= MAX_GALLERY}
                />
              </label>
            </div>
            <p className="text-sm text-[#4A4A4A] mb-4">
              Up to {MAX_GALLERY} photos, 3MB each. These appear on your public profile.
            </p>
            {gallery.length === 0 ? (
              <div className="rounded-xl border-2 border-dashed border-gray-300 py-10 text-center text-[#71717A] text-sm">
                No gallery photos yet. Add up to {MAX_GALLERY} to showcase your work.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {gallery.map((url, i) => (
                  <div
                    key={`${url}-${i}`}
                    className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group"
                    data-testid={`gallery-item-${i}`}
                  >
                    <img src={url} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeGalleryItem(i)}
                      className="absolute top-1.5 right-1.5 bg-white/90 hover:bg-white p-1 rounded-full shadow opacity-0 group-hover:opacity-100 transition"
                      data-testid={`remove-gallery-${i}`}
                    >
                      <X className="w-3.5 h-3.5 text-[#1E3A5F]" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Save bar */}
          <div className="sticky bottom-0 bg-white border-t-2 border-[#1E3A5F] py-4 flex items-center justify-end gap-3 -mx-6 px-6 md:-mx-12 md:px-12">
            <Link to="/settings">
              <Button type="button" variant="outline" className="border-gray-300 text-[#4A4A4A]">
                Cancel
              </Button>
            </Link>
            <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-profile-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Save Profile'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PhotoTile = ({ label, url, onUpload, onRemove, uploading, helper, testid }) => (
  <div>
    <h3 className="text-lg font-bold text-[#1E3A5F] mb-2">{label}</h3>
    <p className="text-sm text-[#4A4A4A] mb-3">{helper}</p>
    <div
      className="relative w-32 h-32 rounded-full overflow-hidden border-4 border-[#1E3A5F]/20 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center"
      data-testid={`${testid}-preview`}
    >
      {url ? (
        <>
          <img src={url} alt={label} className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full shadow"
            data-testid={`${testid}-remove`}
          >
            <X className="w-3.5 h-3.5 text-[#1E3A5F]" />
          </button>
        </>
      ) : (
        <ImageIcon className="w-8 h-8 text-[#71717A]" />
      )}
    </div>
    <div className="mt-3">
      <label
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1E3A5F] text-white text-xs font-semibold cursor-pointer hover:bg-[#2d4a6f]"
        data-testid={`${testid}-upload`}
      >
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {url ? 'Replace' : 'Upload'}
        <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
      </label>
    </div>
  </div>
);

export default VendorProfileEdit;
